/**
 * AccountModal — "Minha conta" (S3.7).
 *
 * Mostra identidade (email/role/empresa), permite editar o nome de exibição
 * (PATCH /api/auth/me) e trocar a senha (POST /api/auth/change-password).
 *
 * Validações de UX (front): nova senha mínima e confirmação == nova. O backend
 * é a fonte da verdade (senha atual incorreta, mínimo, etc.). Nunca exibe ou
 * loga a senha. Em caso de sucesso na troca, recomenda re-login.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { changePassword, updateMe } from "../lib/api.js";
import { loadSession, saveSession } from "../lib/auth.js";

const MIN_LEN = 8;

export default function AccountModal({ open, session, onClose, onLogout }) {
  const s = session || loadSession() || {};

  const [name, setName] = useState(s.fullName || "");
  const [nameMsg, setNameMsg] = useState(null); // { type, text }
  const [nameBusy, setNameBusy] = useState(false);

  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwMsg, setPwMsg] = useState(null); // { type, text }
  const [pwBusy, setPwBusy] = useState(false);
  const [changed, setChanged] = useState(false);

  if (!open) return null;

  async function saveName(e) {
    e.preventDefault();
    setNameMsg(null);
    setNameBusy(true);
    try {
      const out = await updateMe(name.trim());
      saveSession({ ...(loadSession() || {}), fullName: out.full_name || "" });
      setNameMsg({ type: "ok", text: "Nome atualizado." });
    } catch (err) {
      setNameMsg({ type: "err", text: err?.message || "Falha ao atualizar o nome." });
    } finally {
      setNameBusy(false);
    }
  }

  async function submitPw(e) {
    e.preventDefault();
    setPwMsg(null);
    if (nw.length < MIN_LEN) {
      setPwMsg({ type: "err", text: `A nova senha deve ter ao menos ${MIN_LEN} caracteres.` });
      return;
    }
    if (nw !== confirm) {
      setPwMsg({ type: "err", text: "A confirmação não corresponde à nova senha." });
      return;
    }
    setPwBusy(true);
    try {
      await changePassword(cur, nw);
      setChanged(true);
      setPwMsg({
        type: "ok",
        text: "Senha alterada com sucesso. Recomendamos sair e entrar novamente.",
      });
      setCur(""); setNw(""); setConfirm("");
    } catch (err) {
      setPwMsg({ type: "err", text: err?.message || "Falha ao trocar a senha." });
    } finally {
      setPwBusy(false);
    }
  }

  const inputCls =
    "w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border " +
    "text-noc-text outline-none focus:border-noc-accent/60";

  // Portal para document.body: evita que o backdrop-blur do <header> (que cria
  // um containing block para position:fixed) ancore o modal no topo. Assim o
  // fixed inset-0 fica relativo à viewport e o modal centraliza de verdade.
  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-md rounded-xl border border-noc-border bg-noc-panel
                      p-5 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-noc-text">Minha conta</h2>
          <button type="button" onClick={onClose}
                  className="text-noc-muted hover:text-noc-text text-lg leading-none">×</button>
        </div>

        {/* Identidade (somente leitura) */}
        <div className="grid grid-cols-3 gap-y-1.5 gap-x-2 text-sm">
          <div className="text-noc-muted">Email</div>
          <div className="col-span-2 text-noc-text font-mono break-all">{s.email || "—"}</div>
          <div className="text-noc-muted">Papel</div>
          <div className="col-span-2 text-noc-text">{s.role || "—"}</div>
          <div className="text-noc-muted">Empresa</div>
          <div className="col-span-2 text-noc-text">{s.tenantName || "—"}</div>
        </div>

        {/* Nome de exibição */}
        <form onSubmit={saveName} className="space-y-2 border-t border-noc-border pt-4">
          <label className="text-xs text-noc-muted">Nome de exibição</label>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Seu nome"
              className={inputCls + " flex-1"}
            />
            <button type="submit" disabled={nameBusy}
                    className="h-9 px-3 rounded-lg border border-noc-border text-noc-text
                               hover:bg-noc-panel2 disabled:opacity-50 text-sm shrink-0">
              {nameBusy ? "…" : "Salvar"}
            </button>
          </div>
          {nameMsg && (
            <div className={nameMsg.type === "ok" ? "text-xs text-noc-ok" : "text-xs text-noc-err"}>
              {nameMsg.text}
            </div>
          )}
        </form>

        {/* Trocar senha */}
        <form onSubmit={submitPw} className="space-y-3 border-t border-noc-border pt-4">
          <div className="text-sm font-medium text-noc-text">Trocar senha</div>
          <input type="password" autoComplete="current-password" value={cur}
                 onChange={(e) => setCur(e.target.value)} placeholder="Senha atual"
                 className={inputCls} />
          <input type="password" autoComplete="new-password" value={nw}
                 onChange={(e) => setNw(e.target.value)} placeholder={`Nova senha (mín. ${MIN_LEN})`}
                 className={inputCls} />
          <input type="password" autoComplete="new-password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} placeholder="Confirmar nova senha"
                 className={inputCls} />
          {pwMsg && (
            <div className={
              pwMsg.type === "ok"
                ? "text-sm text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded-lg px-3 py-2"
                : "text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg px-3 py-2"
            }>
              {pwMsg.text}
            </div>
          )}
          {!changed ? (
            <button type="submit" disabled={pwBusy || !cur || !nw || !confirm}
                    className="w-full h-10 rounded-lg bg-noc-accent text-white font-medium
                               hover:opacity-90 disabled:opacity-50">
              {pwBusy ? "Trocando…" : "Trocar senha"}
            </button>
          ) : (
            <button type="button" onClick={() => onLogout?.()}
                    className="w-full h-10 rounded-lg border border-noc-border text-noc-text
                               font-medium hover:bg-noc-panel2">
              Sair e entrar de novo
            </button>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
}
